import { AGENT_READINESS_CHECKS } from "~/lib/siteglue/agent-readiness/registry";
import { AEO_VERSION, AGENT_READINESS_VERSION, CRO_V5_VERSION } from "~/lib/siteglue/agent-readiness/types";
import { CRO_BENCHMARKS } from "~/lib/siteglue/agent-readiness/cro-v5";
import { CRO_PUBLIC_CHANNELS } from "~/lib/siteglue/agent-readiness/cro-channels";
import { CONVERSION_FLOW_CHECK_IDS } from "~/lib/siteglue/agent-readiness/conversion-flow";
import { benchmarksForService } from "~/lib/siteglue/benchmarks/catalog";
import type { ScanReportKind } from "~/lib/siteglue/agent-readiness/selection";
import type { HomepagePreviewSlideId } from "~/lib/siteglue/landing-redesign/homepage-hero";
import {
  AUDIT_AEO_CTA,
  AUDIT_AEO_HEADLINE,
  AUDIT_AEO_HREF,
  AUDIT_AGENTS_TEST_HREF,
  AUDIT_ARO_CTA,
  AUDIT_ARO_HEADLINE,
  AUDIT_ARO_HREF,
  AUDIT_CRO_CTA,
  AUDIT_CRO_HEADLINE,
  AUDIT_CRO_HREF,
  AUDIT_INDEX_HREF,
  BENCHMARK_LIBRARY_HREF,
  SCAN_REPORT_AEO_HELP,
  SCAN_REPORT_AGENTS_TEST_HELP,
  SCAN_REPORT_ARO_HELP,
  SCAN_REPORT_CRO_HELP,
} from "~/lib/siteglue/landing-redesign/copy";
import { publicPricingCard, publicPricingFaq } from "~/lib/siteglue/public-pricing";

export type AuditPageSlug =
  | "agent-readiness"
  | "conversion-optimization"
  | "answer-engine-optimization"
  | "ai-agent-testing";

export type AuditFaq = { q: string; a: string };

export type AuditNamedItem = { title: string; body: string; href?: string };

export type AuditPageContent = {
  slug: AuditPageSlug | "index";
  href: string;
  title: string;
  eyebrow: string;
  headline: string;
  ctaLabel: string;
  startingPriceLabel?: string;
  scanReports: ScanReportKind[];
  description: string;
  what: string;
  measures: string;
  why: string;
  who: string;
  methodology: string;
  formula: string;
  findings: string[];
  improvements: string[];
  faqs: AuditFaq[];
  benchmarks?: AuditNamedItem[];
  channels?: AuditNamedItem[];
  examples?: { pass: string; fail: string }[];
  secondaryCta?: { href: string; label: string };
  plannedNote?: string;
  demoTab?: HomepagePreviewSlideId;
};

export const AUDIT_LEGACY_REDIRECTS: Record<string, string> = {
  "ai-agent-readiness": AUDIT_ARO_HREF,
  "website-conversion": AUDIT_CRO_HREF,
  "communication-channels": AUDIT_CRO_HREF,
  "test-with-ai-agents": AUDIT_AGENTS_TEST_HREF,
};

const ARO_LIBRARY = benchmarksForService("aro");
const CRO_LIBRARY = benchmarksForService("cro").filter((item) =>
  CRO_BENCHMARKS.some((benchmark) => benchmark.id === item.id),
);
const AEO_LIBRARY = benchmarksForService("aeo");

export const AUDIT_SERVICE_PAGES: AuditPageContent[] = [
  {
    slug: "answer-engine-optimization",
    href: AUDIT_AEO_HREF,
    demoTab: "aeo",
    title: "AEO — Answer Engine Optimization",
    eyebrow: "AEO",
    headline: AUDIT_AEO_HEADLINE,
    ctaLabel: AUDIT_AEO_CTA,
    startingPriceLabel: publicPricingCard("aeo").priceLabel,
    scanReports: ["aeo"],
    description: SCAN_REPORT_AEO_HELP,
    what: "AEO measures how AI answer engines discover, understand, cite, and recommend your business. It is an optimization service around SiteGlue Agent, not a separate AI Agent product.",
    measures: `Six AEO benchmarks: ${AEO_LIBRARY.map((item) => item.name).join(", ")}. Version ${AEO_VERSION}. Technical entity clarity can be read from your site. Answer-engine visibility stays Not verified until a live labeled test runs.`,
    why: "People ask ChatGPT, Gemini, and similar engines who to call. If those engines cannot name you accurately, they recommend someone else.",
    who: "Operators who want to know whether answer engines can recommend the business — without fake ChatGPT claims.",
    methodology: `Catalog ${AEO_VERSION}. Planned visibility checks stay Not verified. SiteGlue never claims a named engine recommended you without a labeled live test on the report.`,
    formula: "Public AEO score is null until live labeled answer-engine tests exist. Not verified is never scored as 0. Technical entity and content clarity is recorded as evidence, not as a ChatGPT recommendation.",
    plannedNote: "Brand mentions, citations, share of voice, and answer accuracy are planned. They display as Not verified until evidence-backed tests run.",
    benchmarks: AEO_LIBRARY.map((item) => ({
      title: `${item.name}${item.status === "planned" ? " (planned)" : ""}`,
      body: item.what,
      href: `/resources/benchmarks/${item.slug}`,
    })),
    examples: [
      {
        pass: "A live labeled test shows the engine citing the business for a documented query, and public pages name the organization clearly.",
        fail: "A marketing page claims “ChatGPT recommends us” with no live test, or the site has no machine-readable identity.",
      },
    ],
    findings: [
      "Entity and content clarity from public JSON-LD and page copy",
      "Answer visibility, mentions, citations, share of voice, and accuracy stay Not verified without a live labeled test",
      "Technical AEO readiness is not the same as being recommended by ChatGPT",
    ],
    improvements: [
      "Publish consistent Organization, service, and contact markup",
      "Keep hours and NAP facts identical across pages",
      `Review the ${BENCHMARK_LIBRARY_HREF.replace("/resources/", "")} library for each AEO check`,
    ],
    faqs: [
      {
        q: "Will this tell me if ChatGPT recommends my business?",
        a: "Only after a live labeled test is on the report. Until then the visibility checks stay Not verified. We do not invent a recommendation.",
      },
      {
        q: "Is AEO the same as ARO?",
        a: "No. ARO is whether personal AI agents can discover and use your site. AEO is whether answer engines cite and recommend you.",
      },
      publicPricingFaq("aeo"),
    ],
  },
  {
    slug: "agent-readiness",
    href: AUDIT_ARO_HREF,
    demoTab: "aro",
    title: "ARO — Agent Readiness Optimization",
    eyebrow: "ARO",
    headline: AUDIT_ARO_HEADLINE,
    ctaLabel: AUDIT_ARO_CTA,
    startingPriceLabel: publicPricingCard("aro").priceLabel,
    scanReports: ["aro"],
    description: SCAN_REPORT_ARO_HELP,
    what: "Agent Readiness Optimization scores whether personal AI agents can discover your business, understand its services, authenticate when needed, and reach supported actions.",
    measures: `We score ${AGENT_READINESS_CHECKS.length} technical checks across agent discovery and crawlability, structured business knowledge, MCP and tool discovery, authentication and authorization, and agent action readiness. Version ${AGENT_READINESS_VERSION}. The SiteGlue WordPress plugin can publish supported ARO artifacts. Test With AI Agents is optional and does not change the ARO score.`,
    why: "If agents cannot discover or use your site, they skip you or invent answers. A ready site lets people and their AI agents get answers, take action, and reach the right person at your company.",
    who: "Operators who want to know whether Claude, Grok, and other agents can find hours, contact paths, and actions from public pages.",
    methodology: `Catalog ${AGENT_READINESS_VERSION}. ${AGENT_READINESS_CHECKS.length} checks. Unselected or untested items stay Not verified, never a fake 0. ARO is independent of CRO and AEO.`,
    formula: "Weighted pass rate of applicable ARO checks. Weight 0 and Not verified / Not applicable do not pull the score toward 0.",
    benchmarks: ARO_LIBRARY.map((item) => ({
      title: item.name,
      body: item.why,
      href: `/resources/benchmarks/${item.slug}`,
    })),
    examples: [
      {
        pass: "robots.txt, sitemap, llms.txt or Markdown, structured business facts, and an MCP or contact action are readable.",
        fail: "Crawlers are blocked, there is no machine-readable business information, and no agent-accessible action exists.",
      },
    ],
    findings: [
      "Agent discovery, robots.txt, llms.txt, Markdown, and structured business information",
      "MCP discovery, HTTP Link headers, authentication, and agent tools",
      "Task readiness when an action is published",
    ],
    improvements: [
      "Publish crawl and knowledge files agents can read",
      "Expose a machine-readable way to contact or book",
      "Install the SiteGlue WordPress plugin for supported ARO artifacts, or copy install snippets for other platforms",
    ],
    faqs: [
      {
        q: "Is this a live test of Claude or ChatGPT?",
        a: "No. The ARO score is a website audit. Live model walks are optional under Test With AI Agents.",
      },
      {
        q: "Does a low ARO score mean my site is broken for people?",
        a: "No. ARO is for agent access. CRO covers conversion for people across forms, chat, calls, SMS, and email.",
      },
      publicPricingFaq("aro"),
    ],
    secondaryCta: { href: AUDIT_AGENTS_TEST_HREF, label: "Test With AI Agents" },
  },
  {
    slug: "conversion-optimization",
    href: AUDIT_CRO_HREF,
    demoTab: "cro",
    title: "CRO — Conversion Rate Optimization",
    eyebrow: "CRO",
    headline: AUDIT_CRO_HEADLINE,
    ctaLabel: AUDIT_CRO_CTA,
    startingPriceLabel: publicPricingCard("cro").priceLabel,
    scanReports: ["cro"],
    description: SCAN_REPORT_CRO_HELP,
    what: "Conversion Rate Optimization scores whether the website turns visitors into customers — from first impression through confirmation across website forms and SiteGlue Agent channels.",
    measures: `One CRO score from five equally weighted benchmarks: ${CRO_BENCHMARKS.map((item) => item.label).join(", ")}. Conversion flows are evaluated across website forms, AI Agents, Live Chat, Browser Calls, Phone Calls, Text Messages, and Email. Publicly detectable channels are scored automatically — you do not configure every channel before the audit.`,
    why: "People still need a first impression, a next step, proof, a usable mobile path, and a complete conversion flow. A published phone number or an enabled SiteGlue channel is not a completed journey.",
    who: "Marketing and operations teams who want conversion evidence across website and AI channels, not a generic SEO quiz.",
    methodology: `Version ${CRO_V5_VERSION}. Equal mean of eligible benchmarks (the 20% model). Not verified is excluded, never scored as 0. Conversion Flow is never verified from an entry point alone. ${CONVERSION_FLOW_CHECK_IDS.length} flow checks. Historical People Ready and Channel Ready scores are not relabeled as CRO.`,
    formula: "Equal mean of eligible benchmarks. A benchmark needs coverage of at least 50% and at least two conclusive checks. Overall score needs at least two eligible benchmarks.",
    benchmarks: CRO_LIBRARY.map((item) => ({
      title: item.name,
      body: item.what,
      href: `/resources/benchmarks/${item.slug}`,
    })),
    channels: CRO_PUBLIC_CHANNELS.map((item) => ({ title: item.label, body: item.question })),
    examples: [
      {
        pass: "Visitor → AI Agent → Intent Identification → Appointment Booking → Confirmation, with human handoff and callback when needed.",
        fail: "A phone number, chat launcher, or enabled SiteGlue channel is treated as a finished conversion journey.",
      },
    ],
    findings: [
      "First impression, CTA, trust, and mobile evidence from the public pages",
      "Channel findings for forms, AI Agents, live chat, browser calls, phone, SMS, and email",
      "Conversion Flow steps past entry stay not verified until they are actually tested",
    ],
    improvements: [
      "Make the next step obvious and reachable",
      "Close dead ends between intent and confirmation",
      "Use the Improve rail on your report for prioritized fixes",
    ],
    faqs: [
      {
        q: "Is this the old Website Conversion or Channels score?",
        a: "No. Those historical scores stay on old reports. New scans use cro-v5.0. Directory only shows CRO when that version is present.",
      },
      {
        q: "Does turning on SiteGlue phone or chat raise my CRO score?",
        a: "Not by itself. Enabling a channel is not a Conversion Flow pass. We evaluate the customer experience, and untested steps stay not verified.",
      },
      {
        q: "What happened to HRO?",
        a: "HRO is not a customer-facing category. Use ARO, CRO, and AEO.",
      },
      publicPricingFaq("cro"),
    ],
  },
];

export const AUDIT_PAGES: AuditPageContent[] = [
  ...AUDIT_SERVICE_PAGES,
  {
    slug: "ai-agent-testing",
    href: AUDIT_AGENTS_TEST_HREF,
    title: "Test With AI Agents",
    eyebrow: "Walks",
    headline: "See what an agent actually did.",
    ctaLabel: "Test With AI Agents",
    scanReports: ["agents-test"],
    description: SCAN_REPORT_AGENTS_TEST_HELP,
    what: "Optional walks against a task. This is a testing feature, not a fourth optimization service.",
    measures: "Optional walks against a task. Outcomes are Live test, Simulation, Unsupported, or Inconclusive.",
    why: "A score does not show what an agent actually did. A walk shows the path, the answer, and the gap — without claiming a consumer app ran unless it did.",
    who: "Teams that want to see a task completed or fail, then copy a fix prompt.",
    methodology: "Claude and Grok can run as models on SiteGlue when a key is present. Other named agents use a scan simulation. That is not the consumer app. Walks do not change ARO, CRO, or AEO scores.",
    formula: "Walks are not scored. They never change ARO, CRO, or AEO.",
    findings: [
      "Live test: a model on SiteGlue fetched public pages",
      "Simulation: SiteGlue replayed the scan, not the consumer app",
      "Unsupported or Inconclusive when there is no runtime or not enough pages",
    ],
    improvements: [
      "Fix the pages the walk could not complete",
      "Do not treat a simulation as a Claude or Muse app test",
      "Use Improve Your Score for the underlying audit gaps",
    ],
    faqs: [
      {
        q: "Did Claude test my site?",
        a: "Only if the result is labeled Live test and the agent was Claude. Otherwise it is a simulation or unsupported.",
      },
      {
        q: "Does a walk change my scores?",
        a: "No. Walks are a separate capability. ARO, CRO, and AEO stay independent.",
      },
    ],
  },
];

export const AUDIT_INDEX = {
  href: AUDIT_INDEX_HREF,
  title: "SiteGlue Optimization Audits",
  description:
    "Three optimization services around one product — SiteGlue Agent. Get recommended (AEO), get discovered and connected (ARO), and convert visitors into customers (CRO).",
};

export function auditPageBySlug(slug: string): AuditPageContent | undefined {
  return AUDIT_PAGES.find((page) => page.slug === slug);
}

export const AUDIT_PATHS = [AUDIT_INDEX_HREF, ...AUDIT_PAGES.map((page) => page.href)];
